import { useState, useEffect } from 'react';
import { supabase } from '../../services/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useData } from '../../contexts/DataContext';
import { ConfirmationModal } from '../shared/ConfirmationModal';
import { Delete, Edit } from '@mui/icons-material';
import { usePermissions } from '../../contexts/PermissionsContext';
import { RoleChangeModal } from '../shared/RoleChangeModal';
import * as Sentry from '@sentry/react';
import { extractErrorMessageFromResponse } from '../../services/errorService';
import { useTranslation } from 'react-i18next';
import DataTable from '../shared/DataTable';
import { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import { InviteUserModal } from '../shared/InviteUserModal';
import { formatDateTime } from '../../utils/dateUtils';

interface UserManagementProps {
  companyId?: string;
  isSystemLevel?: boolean;
}

interface User {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string;
  app_metadata: {
    isSuperAdmin?: boolean;
  };
  user_metadata: {
    isAdmin?: boolean;
    first_name?: string;
    last_name?: string;
    [key: string]: unknown; // Allow for additional user metadata fields
  };
}

export const UserManagement = ({ companyId, isSystemLevel = false }: UserManagementProps) => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const currentUserId = user?.id || null;
  const [modalOpen, setModalOpen] = useState(false);
  const [inviting, setInviting] = useState(false); // State to track inviting status
  const { showNotification } = useNotification();
  const { isSuperAdmin, hasAdminAccess } = usePermissions();
  const { targetCompany, setIsLoading } = useData();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [roleChangeModalOpen, setRoleChangeModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const columnDefs: ColDef<User>[] = [
    {
      headerName: t('user_management.name'),
      sortable: true,
      filter: true,
      valueFormatter: (params: ValueFormatterParams<User>) => {
        return `${params.data?.user_metadata.first_name || '-'} ${params.data?.user_metadata.last_name || '-'}`;
      },
      cellStyle: {
        cursor: 'pointer',
        color: '#3b82f6', // blue-500 color in Tailwind
      },
      onCellClicked: (params) => {
        setSelectedUser(params.data as User);
        setRoleChangeModalOpen(true);
      },
    },
    {
      field: 'email',
      headerName: t('user_management.email'),
      sortable: true,
      filter: true,
    },
    {
      headerName: t('user_management.role'),
      sortable: true,
      filter: true,
      valueFormatter: (params: ValueFormatterParams<User>) => {
        if (!params.data) return '';
        return params.data.app_metadata.isSuperAdmin
          ? t('user_management.super_admin')
          : params.data.user_metadata.isAdmin
            ? t('user_management.admin')
            : t('user_management.user');
      },
    },
    {
      field: 'created_at',
      headerName: t('user_management.joined'),
      sortable: true,
      filter: true,
      valueFormatter: (params: ValueFormatterParams<User>) => {
        return params.data ? formatDateTime(params.data.created_at) : '-';
      },
      sort: 'desc',
    },
    {
      field: 'last_sign_in_at',
      headerName: t('user_management.last_active'),
      sortable: true,
      filter: true,
      valueFormatter: (params: ValueFormatterParams<User>) => {
        return params.data && params.data.last_sign_in_at
          ? formatDateTime(params.data.last_sign_in_at)
          : '-';
      },
    },
    {
      headerName: t('user_management.actions'),
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams<User>) => {
        if (!params.data) return null;
        return canRemoveOrInviteUser() && currentUserId !== params.data.id ? (
          <div className="flex space-x-2">
            <button
              onClick={() => handleEditClick(params.data as User)}
              className="text-blue-600 hover:text-blue-800"
              title={t('user_management.edit_role')}
            >
              <Edit className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleDeleteClick(params.data?.id as string)}
              className="text-red-600 hover:text-red-800"
              title={t('user_management.delete_user')}
            >
              <Delete className="h-4 w-4" />
            </button>
          </div>
        ) : null;
      },
    },
  ];

  const gridConfig = {
    pagination: true,
    // paginationPageSize: 50,
    paginationAutoPageSize: true,
    defaultColDef: {
      flex: 1,
      minWidth: 100,
      resizable: true,
    },
  };

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const { data, error: fetchError } = await supabase.functions.invoke('user-management', {
        method: 'POST',
        body: JSON.stringify({
          method: 'GET',
          operation: 'list',
          // Send companyId if not system level
          companyId: !isSystemLevel ? companyId || targetCompany : undefined,
        }),
      });

      if (fetchError) {
        const errorMessage = await extractErrorMessageFromResponse(fetchError, t);
        showNotification(errorMessage, 'error');
        Sentry.captureException(errorMessage);
        throw new Error(errorMessage);
      }

      setUsers(data.users.users || []);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      showNotification(t('user_management.failed_to_fetch_users'), 'error');
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    setIsLoading(true);
    try {
      const { error: deleteError } = await supabase.functions.invoke('user-management', {
        method: 'POST',
        body: JSON.stringify({
          method: 'DELETE',
          operation: 'delete',
          userData: { id: userId },
          // Send companyId if not system level
          companyId: !isSystemLevel ? companyId || targetCompany : undefined,
        }),
      });

      if (deleteError) {
        const errorMessage = await extractErrorMessageFromResponse(deleteError, t);
        showNotification(errorMessage, 'error');
        throw new Error(errorMessage);
      }

      // Refresh the users list after deletion
      await fetchUsers();
      showNotification('User removed successfully', 'success');
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      Sentry.captureException(err);
      showNotification('Failed to remove user', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (userId: string) => {
    setUserToDelete(userId);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (userToDelete) {
      handleDeleteUser(userToDelete);
    }
    setDeleteConfirmOpen(false);
    setUserToDelete(null);
  };

  const handleInviteUser = async (userData: unknown) => {
    setIsLoading(true);
    setInviting(true);
    try {
      const { error: inviteError } = await supabase.functions.invoke('user-management', {
        method: 'POST',
        body: JSON.stringify({ method: 'POST', operation: 'create', userData }),
      });
      if (inviteError) {
        Sentry.captureException(inviteError);
        throw inviteError;
      }

      await fetchUsers();
      setModalOpen(false);
      showNotification('User invited successfully', 'success');
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      Sentry.captureException(err);
      showNotification('Failed to invite user', 'error');
    } finally {
      setIsLoading(false);
      setInviting(false);
    }
  };

  const handleRoleChange = async (newRole: string) => {
    if (!selectedUser) return;
    setIsLoading(true);
    try {
      const targetRole =
        newRole === '' ? (isSystemLevel ? 'Super Admin' : 'Company Admin') : newRole;
      const roleValue = targetRole === 'Super Admin' || targetRole === 'Company Admin';
      const roleType = isSystemLevel ? 'superAdmin' : 'companyAdmin';

      const { error: roleError } = await supabase.functions.invoke('user-management', {
        method: 'POST',
        body: JSON.stringify({
          method: 'POST',
          operation: 'changeRole',
          userData: { id: selectedUser.id },
          companyId: !isSystemLevel ? companyId || targetCompany : undefined,
          newRole: {
            type: roleType,
            value: roleValue,
          },
        }),
      });

      if (roleError) throw roleError;

      await fetchUsers();
      showNotification('User role updated successfully', 'success');
    } catch (err) {
      Sentry.captureException(err);
      showNotification('Failed to update user role', 'error');
    } finally {
      setIsLoading(false);
      setRoleChangeModalOpen(false);
      setSelectedUser(null);
    }
  };

  const handleEditClick = (user: User) => {
    setSelectedUser(user);
    setRoleChangeModalOpen(true);
  };

  useEffect(() => {
    fetchUsers();
  }, [companyId, isSystemLevel]);

  const canRemoveOrInviteUser = () => {
    return isSystemLevel ? isSuperAdmin : hasAdminAccess();
  };

  return (
    <div className="flex flex-col h-full">
      {canRemoveOrInviteUser() && (
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">{t('user_management.users')}</h3>
          <button
            onClick={() => setModalOpen(true)}
            className="px-2 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            {t('user_management.invite_user')}
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow flex-grow min-h-0">
        <DataTable<User>
          rowData={users}
          columnDefs={columnDefs}
          gridConfig={gridConfig}
          isLoading={loading}
          autoHeight={false}
          height={'100%'}
          theme="ag-theme-alpine"
          noRowsMessage={t('user_management.no_users')}
        />
      </div>

      <InviteUserModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onInvite={handleInviteUser}
        isSystemLevel={isSystemLevel}
        companyId={companyId}
        inviting={inviting}
      />

      <ConfirmationModal
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title={t('user_management.confirm_user_deletion')}
        message={
          isSystemLevel
            ? t('user_management.delete_user_from_all_companies')
            : t('user_management.delete_user_from_current_company')
        }
      />

      <RoleChangeModal
        open={roleChangeModalOpen}
        onClose={() => {
          setRoleChangeModalOpen(false);
          setSelectedUser(null);
        }}
        onSave={handleRoleChange}
        currentRole={
          selectedUser
            ? isSystemLevel
              ? selectedUser.app_metadata.isSuperAdmin
                ? t('user_management.super_admin')
                : t('user_management.user')
              : selectedUser.user_metadata.isAdmin
                ? t('user_management.company_admin')
                : t('user_management.company_user')
            : ''
        }
        isSystemLevel={isSystemLevel}
      />
    </div>
  );
};
